// src/pages/OltPerfiles.jsx — nuestra configuración para dar de alta, comparada con la OLT
//
// La base guarda el CATÁLOGO: perfiles DBA, de línea, de servicio, WAN,
// TR-069, perfiles de puertos por modelo y los planes. Esta página enseña
// SOLO eso, y cada cosa con un estado: igual en la OLT, distinto (diciendo
// qué) o falta. Lo que la OLT tenga de más —los DBA de fábrica— no aparece.
//
// Crear algo manda los comandos a la OLT y lo guarda en el catálogo. Lo que
// está distinto no se corrige solo: un perfil de línea en uso lo tienen
// cientos de ONTs y un `commit` les llega a todas a la vez.
//
// Los comandos que se enseñan son la vista previa; los arma de verdad
// services/olt_catalogo_service.py.
//
// UNA OLT NUEVA SE ARMA EN ORDEN. Mientras no tenga sus perfiles de alta,
// arriba sale la guía: DBA → perfil de línea → servicio → WAN → TR-069 →
// modelos → planes → perfiles de las altas. Lo que depende de algo que
// falta no se deja crear y dice qué va primero; el backend lo vuelve a
// comprobar contra la OLT antes de mandar nada. Los nombres se proponen
// por servicio (INTERNET, VOZ, IPTV) y se pueden cambiar al crear.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Boton, Iconos } from "../componentes/base";
import NubeParticulas from "../componentes/espera/NubeParticulas";
import "./OltPerfiles.css";

/* Lo que de verdad se le pregunta a la OLT en esta pantalla, para la
   espera. Cada uno es un comando `display`. */
const LECTURAS_CONFIG = [
  { icono: Iconos.potencia, rotulo: "DBA" },
  { icono: Iconos.senal, rotulo: "Perfiles de línea" },
  { icono: Iconos.documento, rotulo: "Perfiles de servicio" },
  { icono: Iconos.planes, rotulo: "Tablas de tráfico" },
  { icono: Iconos.onus, rotulo: "Modelos de ONT" },
  { icono: Iconos.antena, rotulo: "WAN y TR-069" },
];

/* ================= comandos, en espejo del backend ================= */

export const TIPOS_DBA = {
  type1: { campos: ["fijo"], texto: "fijo", guia: "Ese ancho queda reservado siempre, se use o no. Para gestión." },
  type2: { campos: ["asegurado"], texto: "asegurado", guia: "Se garantiza cuando el servicio lo pide; si no lo usa, lo aprovechan otros. Para voz e IPTV." },
  type3: { campos: ["asegurado", "maximo"], texto: "asegurado + máximo", guia: "Tiene lo asegurado y puede subir hasta el máximo si sobra. Para empresas." },
  type4: { campos: ["maximo"], texto: "máximo", guia: "Lo que sobre, hasta ese tope. Para Internet: la velocidad del cliente la pone la tabla de tráfico." },
  type5: { campos: ["fijo", "asegurado", "maximo"], texto: "fijo + asegurado + máximo", guia: "Las tres a la vez. Casi nunca hace falta." },
};
const PALABRA = { fijo: "fix", asegurado: "assure", maximo: "max" };

export function comandosDba(f) {
  const partes = TIPOS_DBA[f.tipo].campos.map((c) => `${PALABRA[c]} ${Number(f[c]) || 0}`).join(" ");
  return [`dba-profile add profile-id ${f.numero} profile-name "${f.nombre}" ${f.tipo} ${partes}`];
}

export function rango(texto) {
  const fuera = new Set();
  String(texto ?? "").split(",").forEach((t) => {
    const m = t.trim().match(/^(\d)(?:-(\d))?$/);
    if (!m) return;
    for (let p = Number(m[1]); p <= Number(m[2] ?? m[1]); p += 1) if (p <= 7) fuera.add(p);
  });
  return [...fuera].sort((a, b) => a - b);
}

/** El perfil de línea nuevo. Con prioridad, Internet se queda las que sobran. */
export function comandosLinea(f, vlansPlanes) {
  const servicios = f.servicios;
  const c = [
    `ont-lineprofile gpon profile-id ${f.numero} profile-name "${f.nombre}"`,
    "  fec-upstream enable", "  tr069-management enable", `  mapping-mode ${f.modo}`,
    ...servicios.map((s) => `  tcont ${s.tcont} dba-profile-id ${s.dba}`),
    ...servicios.map((s) => `  gem add ${s.gem} eth tcont ${s.tcont}`),
  ];
  servicios.forEach((s) => {
    if (f.modo === "priority") {
      rango(s.prioridades).forEach((p, k) => c.push(`  gem mapping ${s.gem} ${k} priority ${p}`));
    } else {
      const vlans = s.vlan ? [s.vlan] : [...vlansPlanes].sort((a, b) => a - b);
      vlans.forEach((v, k) => c.push(`  gem mapping ${s.gem} ${k} vlan ${v}`));
    }
  });
  c.push("  commit", "  quit");
  return c;
}

export const comandosServicio = (f) => [
  `ont-srvprofile gpon profile-id ${f.numero} profile-name "${f.nombre}"`,
  `  ont-port eth adaptive ${f.eth}${f.pots ? " pots adaptive" : ""}`,
  "  ring check enable", "  commit",
];

export const comandosWan = (f) => [
  `ont wan-profile profile-id ${f.numero} profile-name "${f.nombre}"`,
  f.nat ? "  nat enable" : "  nat disable", "  quit",
];

export const comandosTr069 = (f) => [
  `ont tr069-server-profile add profile-id ${f.numero} profile-name "${f.nombre}" url "${f.url}"`,
];

export function puertosDe(eth, banda) {
  return { eth: Number(eth) === 1 ? "1" : `1-${eth}`, wlan: banda === "doble" ? "1,5" : banda === "simple" ? "1" : "" };
}

export function comandosModelo(f) {
  const { eth, wlan } = puertosDe(f.eth, f.banda);
  return [
    `ont policy-route-profile profile-id ${f.id} profile-name "${f.modelo}"`,
    `  policy-route 0 port-based eth ${eth}${wlan ? ` wlan ${wlan}` : ""} wan 0`, "  quit",
  ];
}

export function ordenTraffic(indice, mbps, existe) {
  const cir = Number(mbps) * 1024;
  const cbs = cir * 32 + 2000;
  const valores = `cir ${cir} cbs ${cbs} pir ${cir} pbs ${cbs}`;
  return existe
    ? `traffic table ip modify index ${indice} ${valores}`
    : `traffic table ip index ${indice} name "${mbps}M" ${valores} color-mode color-blind priority 4 priority-policy local-setting`;
}

/* ================= la guía de una OLT nueva ================= */

const falta = (x) => x.estado === "falta";

/** Los pasos para que una OLT pueda dar altas, en el orden en que dependen
    unos de otros. `estado`: hecho · siguiente · falta · bloqueado. */
export function pasosDeAlta(datos) {
  const S = datos.secciones;
  const lista = (l) => l.length > 0 && !l.some(falta);
  const pasos = [
    { clave: "dba", seccion: "perfiles", titulo: "Perfil DBA", texto: "Cuánto ancho de subida reserva cada T-CONT.", items: S.dba, hecho: lista(S.dba) },
    {
      clave: "linea", seccion: "perfiles", titulo: "Perfil de línea", texto: "Internet por su T-CONT, con su DBA y su GEM.",
      items: S.linea, hecho: lista(S.linea),
      primero: S.linea.find((l) => falta(l) && l.requiere?.length)?.requiere,
    },
    { clave: "servicio", seccion: "perfiles", titulo: "Perfil de servicio", texto: "Los puertos que se le configuran a la ONT.", items: S.servicio, hecho: lista(S.servicio) },
    { clave: "wan", seccion: "gestion", titulo: "Perfil WAN", texto: "La conexión PPPoE de la ONT, en ruta.", items: S.wan, hecho: lista(S.wan) },
  ];
  if (datos.tr069Activo !== false) {
    pasos.push({ clave: "tr069", seccion: "gestion", titulo: "Servidor TR-069", texto: "Para gestionar las ONTs en remoto. Si esta OLT no tiene, marca «No».", items: S.tr069, hecho: lista(S.tr069) });
  }
  pasos.push({ clave: "modelos", seccion: "modelos", titulo: "Modelos de ONT", texto: "Qué puertos y qué WiFi salen a internet en cada modelo.", items: S.modelos, hecho: lista(S.modelos) });

  const conUplink = datos.vlans.some((v) => v.uplink);
  pasos.push({
    clave: "planes", seccion: "planes", titulo: "Planes", texto: "Megas, precio y VLAN; cada uno con su tabla de tráfico.",
    items: S.planes, hecho: lista(S.planes),
    primero: conUplink ? null : ["una VLAN con su uplink, en la sección Equipo"],
  });

  const p = datos.propuestaAltas;
  const linea = p && S.linea.find((l) => l.numero === p.lineprofile);
  const servicio = p && S.servicio.find((x) => x.numero === p.srvprofile);
  const pendientes = [
    linea?.estado !== "ok" && (linea ? `el perfil de línea ${linea.numero} «${linea.nombre}»` : "un perfil de línea en el catálogo"),
    servicio?.estado !== "ok" && (servicio ? `el perfil de servicio ${servicio.numero} «${servicio.nombre}»` : "un perfil de servicio en el catálogo"),
  ].filter(Boolean);
  // El último de verdad: fijarlos es lo que hace que la OLT deje de ser
  // «nueva» y la guía se esconda, así que no puede quedar nada detrás.
  const antes = pasos.filter((x) => !x.hecho).map((x) => x.titulo);
  const listaTexto = (l) => (l.length > 1 ? `${l.slice(0, -1).join(", ")} y ${l[l.length - 1]}` : l[0]);
  pasos.push({
    clave: "altas", titulo: "Perfiles de las altas", texto: "Con qué perfil de línea, de servicio y GEM da de alta esta OLT.",
    hecho: datos.globales?.lineprofile != null,
    primero: antes.length ? [listaTexto(antes)] : pendientes.length ? pendientes : null,
  });

  let siguiente = false;
  return pasos.map((x, i) => {
    const estado = x.hecho ? "hecho" : x.primero ? "bloqueado" : siguiente ? "falta" : "siguiente";
    if (!x.hecho) siguiente = true;
    return { ...x, numero: i + 1, estado };
  });
}

const TIPO_TITULO = { dba: "Perfil DBA", linea: "Perfil de línea", servicio: "Perfil de servicio", wan: "Perfil WAN", tr069: "Servidor TR-069" };

function GuiaAlta({ pasos, setSeccion, setPanel, ocupado }) {
  const quedan = pasos.filter((x) => x.estado !== "hecho").length;
  return (
    <section className="cat-tarjeta cat-guia" aria-label="Pasos para dar altas">
      <header className="cat-guia__cabeza">
        <h3>Para que esta OLT pueda dar altas</h3>
        <p>{quedan ? `Faltan ${quedan} de ${pasos.length} pasos. Van en orden: cada uno usa lo que dejó el anterior.` : "Todo listo."}</p>
      </header>
      <ol className="cat-pasos">
        {pasos.map((x) => {
          const pendientes = (x.items || []).filter(falta);
          const accion = () => {
            if (x.clave === "altas") setPanel({ tipo: "altas" });
            else if (pendientes.length === 1 && TIPO_TITULO[x.clave]) setPanel({ tipo: "crear", que: x.clave, item: pendientes[0] });
            else setSeccion(x.seccion);
          };
          return (
            <li key={x.clave} className={`cat-paso cat-paso--${x.estado}`}>
              <span className="cat-paso__num" aria-hidden="true">{x.estado === "hecho" ? <Iconos.confirmar size={14} strokeWidth={3} /> : x.numero}</span>
              <div className="cat-paso__texto">
                <b>{x.titulo}</b>
                <span>{x.estado === "bloqueado" ? <>Primero: {x.primero.join(" y ")}.</> : x.texto}</span>
              </div>
              <div className="cat-paso__accion">
                {x.estado === "hecho" && <span className="cat-paso__hecho">Hecho</span>}
                {(x.estado === "siguiente" || x.estado === "falta") && (
                  <Boton className="cat-chico" variante={x.estado === "siguiente" ? "primario" : "secundario"} disabled={ocupado} onClick={accion}>
                    {x.clave === "altas" ? "Usar para las altas" : pendientes.length === 1 && TIPO_TITULO[x.clave] ? "Crear" : "Ir"}
                  </Boton>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ================= piezas ================= */

const numero = (n) => (n == null ? "—" : Number(n).toLocaleString("es-MX"));
const sinEspacios = (v) => v.replace(/\s/g, "");
const PESO = { falta: 3, aviso: 2, ok: 1 };
const peor = (lista) => lista.reduce((a, b) => (PESO[b] > PESO[a] ? b : a), "ok");
const ESTADO = {
  ok: ["igual en la OLT", Iconos.confirmar],
  aviso: ["distinto", Iconos.aviso],
  falta: ["falta en la OLT", Iconos.cerrar],
};

function Estado({ estado, texto }) {
  const [t, I] = ESTADO[estado];
  return <span className={`cat-insignia cat-insignia--${estado}`}><I size={12} strokeWidth={2.8} />{texto || t}</span>;
}

function Ayuda({ texto }) {
  return <span className="cat-ayuda" tabIndex={0} role="note" aria-label={texto} data-ayuda={texto}>?</span>;
}

function Seccion({ icono: I, titulo, cuenta, texto, ayuda, acciones, children }) {
  return (
    <section className="cat-tarjeta">
      <header className="cat-tarjeta__cabecera">
        <span className="cat-tarjeta__icono"><I size={19} strokeWidth={1.9} /></span>
        <div className="cat-tarjeta__titulo">
          <h3>{titulo} <span className="cat-contador">{cuenta}</span>{ayuda && <Ayuda texto={ayuda} />}</h3>
          <p>{texto}</p>
        </div>
        {acciones && <div className="cat-tarjeta__acciones">{acciones}</div>}
      </header>
      {children}
    </section>
  );
}

function Tabla({ columna, filas, vacio }) {
  return (
    <div className="cat-envoltura">
      <table className="cat-tabla">
        <colgroup><col className="c1" /><col className="c2" /><col className="c3" /><col className="c4" /><col className="c5" /></colgroup>
        <thead><tr><th>Nombre</th><th>{columna}</th><th className="n">ONTs</th><th>OLT</th><th /></tr></thead>
        <tbody>
          {filas.length === 0 && <tr><td colSpan={5} className="cat-vacio">{vacio}</td></tr>}
          {filas.map((f) => (
            <tr key={f.clave} className={f.estado !== "ok" ? `cat-fila--${f.estado}` : undefined}>
              <td>
                <div className="cat-nombre">
                  <span className="cat-numero">{f.numero ?? "—"}</span>
                  <span><b>{f.nombre}</b><small>{f.sub}</small></span>
                </div>
              </td>
              <td>
                {f.detalle}
                {f.diferencias?.map((d) => <span key={d} className={`cat-diferencia cat-diferencia--${f.estado}`}>{d}</span>)}
              </td>
              <td className="n">{numero(f.onts)}</td>
              <td>{f.insignia || <Estado estado={f.estado} />}</td>
              <td className="cat-accion">{f.accion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Cadena = ({ trozos }) => (
  <div className="cat-cadena">
    {trozos.map((t, i) => (
      <React.Fragment key={i}>{i > 0 && t.flecha !== false && <i>→</i>}<span>{t.texto}</span></React.Fragment>
    ))}
  </div>
);

/* ================= la página ================= */

export default function OltPerfiles({ oltId }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [seccion, setSeccion] = useState("planes");
  const [panel, setPanel] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await authFetch(`/api/olt/${oltId}/perfiles`);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "No se pudo leer la OLT.");
      setDatos(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [oltId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!mensaje) return undefined;
    const t = setTimeout(() => setMensaje(null), 10000);
    return () => clearTimeout(t);
  }, [mensaje]);

  const mandar = async (ruta, cuerpo, metodo, exito) => {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    try {
      const res = await authFetch(`/api/olt/${oltId}/perfiles${ruta}`, {
        method: metodo, headers: { "Content-Type": "application/json" }, body: JSON.stringify(cuerpo),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "El equipo rechazó el cambio.");
      setMensaje(exito);
      setPanel(null);
      await cargar();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setOcupado(false);
    }
  };

  if (cargando && !datos) {
    return (
      <NubeParticulas
        titulo="Leyendo la OLT"
        nota="Una sola sesión SSH: perfiles, tablas de tráfico, VLANs, modelos y TR-069."
        lecturas={LECTURAS_CONFIG}
        umbral={25}
        aviso="lo normal son unos siete"
      />
    );
  }
  if (!datos) {
    return (
      <div className="cat">
        <p className="sw-aviso sw-aviso--bloqueo"><Iconos.error size={16} strokeWidth={2} />{error}</p>
        <div><Boton icono={Iconos.actualizar} onClick={cargar}>Reintentar</Boton></div>
      </div>
    );
  }

  return (
    <Pagina
      datos={datos} seccion={seccion} setSeccion={setSeccion} cargando={cargando} recargar={cargar}
      error={error} mensaje={mensaje} ocupado={ocupado} panel={panel} setPanel={setPanel} mandar={mandar}
    />
  );
}

function Pagina({ datos, seccion, setSeccion, cargando, recargar, error, mensaje, ocupado, panel, setPanel, mandar }) {
  const { secciones: S, sinPerfil, tr069Activo, catalogoListo } = datos;

  const est = useMemo(() => {
    const e = (lista) => peor(lista.map((x) => x.estado));
    const tr = tr069Activo === false ? [] : S.tr069;
    return {
      // Sin ningún plan no está «todo igual»: le falta lo principal.
      planes: S.planes.length ? e(S.planes) : "falta",
      perfiles: e([...S.dba, ...S.linea, ...S.servicio]),
      modelos: peor([...S.modelos.map((x) => x.estado), ...(sinPerfil.length ? ["falta"] : [])]),
      gestion: e([...S.wan, ...tr]),
      faltan: [...S.dba, ...S.linea, ...S.servicio, ...S.wan, ...tr, ...S.modelos].filter((x) => x.estado === "falta"),
      distintos: [...S.planes, ...S.dba, ...S.linea, ...S.servicio, ...S.wan, ...tr, ...S.modelos].filter((x) => x.estado === "aviso"),
      planesMal: S.planes.filter((x) => x.estado !== "ok"),
    };
  }, [S, sinPerfil, tr069Activo]);

  const problemas = est.faltan.length + est.distintos.length + sinPerfil.length + est.planesMal.filter((p) => p.estado === "falta").length;
  // Una OLT sin perfiles de alta todavía se está armando: manda la guía.
  const nueva = catalogoListo && datos.globales?.lineprofile == null;
  const pasos = nueva ? pasosDeAlta(datos) : [];
  const quedan = pasos.filter((x) => x.estado !== "hecho").length;
  const titulo = !catalogoListo
    ? "Falta aplicar la migración del catálogo"
    : nueva
      ? `Faltan ${quedan} ${quedan === 1 ? "paso" : "pasos"} para dar altas en esta OLT`
    : problemas === 0 ? "Lista para dar de alta"
      : est.faltan.length === 0 && est.distintos.length === 0 && sinPerfil.length
        ? `Falta${sinPerfil.length > 1 ? "n" : ""} ${sinPerfil.length}: ${sinPerfil.map((m) => m.modelo).join(", ")} sin perfil de puertos`
        : `${problemas} ${problemas === 1 ? "cosa no cuadra" : "cosas no cuadran"} con la OLT`;
  const subtitulo = !catalogoListo
    ? "Aplica 015_catalogo_olt.sql en el servidor. Mientras, solo se ven los planes y los modelos."
    : nueva
      ? "Síguelos en el orden de abajo. «Crear lo que falta» hace los perfiles de una vez, ya en orden."
    : problemas === 0 ? "Todo nuestro catálogo está igual en la OLT."
      : est.faltan.length ? `${est.faltan.length} del catálogo no están en esta OLT.`
        : est.distintos.length ? "Hay perfiles distintos: abre cada uno para ver la diferencia."
          : "Sin perfil, el alta de ese modelo se para en la WAN.";

  const tiles = [
    ["planes", Iconos.planes, "Planes", S.planes.length, est.planes],
    ["perfiles", Iconos.potencia, "Perfiles", S.dba.length + S.linea.length + S.servicio.length, est.perfiles],
    ["modelos", Iconos.onus, "Modelos de ONT", S.modelos.length + sinPerfil.length, est.modelos],
    ["gestion", Iconos.antena, "WAN y TR-069", S.wan.length + (tr069Activo === false ? 0 : S.tr069.length), est.gestion],
  ];

  return (
    <div className="cat">
      {error && <p className="sw-aviso sw-aviso--bloqueo"><Iconos.error size={16} strokeWidth={2} />{error}</p>}
      {mensaje && <p className="sw-aviso sw-aviso--exito"><Iconos.confirmar size={16} strokeWidth={2.4} />{mensaje}</p>}

      <section className="cat-tarjeta">
        <div className="cat-veredicto">
          <span className={`cat-veredicto__icono cat-veredicto__icono--${problemas || quedan || !catalogoListo ? "falta" : "ok"}`}>
            {problemas || quedan || !catalogoListo ? <Iconos.aviso size={20} strokeWidth={2} /> : <Iconos.confirmar size={20} strokeWidth={2.4} />}
          </span>
          <div className="cat-veredicto__texto"><h2>{titulo}</h2><p>{subtitulo}</p></div>
          <div className="cat-veredicto__acciones">
            {est.faltan.length > 0 && (
              <Boton icono={Iconos.agregar} disabled={ocupado} onClick={() => setPanel({ tipo: "faltantes" })}>Crear lo que falta</Boton>
            )}
            <Boton variante="secundario" icono={Iconos.actualizar} onClick={recargar} cargando={cargando}>Volver a leer</Boton>
          </div>
        </div>
        <div className="cat-secciones" role="tablist" aria-label="Catálogo">
          {tiles.map(([clave, I, nombre, cuenta, e]) => (
            <button key={clave} type="button" role="tab" aria-selected={seccion === clave}
              className="cat-seccion" onClick={() => setSeccion(clave)}>
              <span className="cat-seccion__icono"><I size={20} strokeWidth={1.9} /></span>
              <span className="cat-seccion__nombre">{nombre}</span>
              <span className="cat-seccion__estado">
                <b>{cuenta}</b><span className={`cat-punto cat-punto--${e}`} /><span>{e === "ok" ? "todo igual" : e === "aviso" ? "hay diferencias" : "falta algo"}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {nueva && <GuiaAlta pasos={pasos} setSeccion={setSeccion} setPanel={setPanel} ocupado={ocupado} />}

      {seccion === "planes" && <Planes datos={datos} setPanel={setPanel} ocupado={ocupado} mandar={mandar} />}
      {seccion === "perfiles" && <Perfiles datos={datos} setPanel={setPanel} ocupado={ocupado} mandar={mandar} />}
      {seccion === "modelos" && <Modelos datos={datos} setPanel={setPanel} ocupado={ocupado} mandar={mandar} />}
      {seccion === "gestion" && <Gestion datos={datos} setPanel={setPanel} ocupado={ocupado} mandar={mandar} />}

      <details className="olt-crudo">
        <summary>Detalles técnicos: lo que contestó la OLT{datos.tiempos ? ` · leído en ${(datos.tiempos.total / 1000).toFixed(1)} s` : ""}</summary>
        {Object.entries(datos.crudo || {}).map(([k, v]) => <pre key={k}>{(v || "").trim()}</pre>)}
      </details>

      {panel && <Panel panel={panel} datos={datos} cerrar={() => setPanel(null)} mandar={mandar} ocupado={ocupado} />}
    </div>
  );
}

/* ================= secciones ================= */

function botonDe(estado, alCrear, alVer, ocupado, primero) {
  if (estado === "falta" && primero?.length) {
    return <span className="cat-bloqueo" title={`Primero: ${primero.join(" y ")}`}>Primero {primero.join(" y ")}</span>;
  }
  return estado === "falta"
    ? <Boton className="cat-chico" icono={Iconos.agregar} onClick={alCrear} disabled={ocupado}>Crear</Boton>
    : <Boton className="cat-chico" variante="secundario" onClick={alVer} disabled={ocupado}>Ver</Boton>;
}

function Planes({ datos, setPanel, ocupado, mandar }) {
  const planes = datos.secciones.planes;
  const tope = Math.max(1, ...planes.map((p) => p.megas));
  const total = planes.reduce((n, p) => n + (p.clientes || 0), 0);
  return (
    <Seccion
      icono={Iconos.planes} titulo="Planes" cuenta={planes.length}
      texto="Nombre, megas y VLAN de cada plan en esta OLT. Los megas se convierten en su tabla de tráfico."
      ayuda="CIR = megas × 1024 · CBS = CIR × 32 + 2000 · PIR = CIR. La VLAN tiene que salir por un uplink; las VLANs se crean en la sección Equipo."
      acciones={<Boton icono={Iconos.agregar} onClick={() => setPanel({ tipo: "plan" })} disabled={ocupado}>Nuevo plan</Boton>}
    >
      <div className="cat-planes">
        {planes.map((p) => (
          <article key={p.id} className={`cat-plan${p.estado !== "ok" ? ` cat-plan--${p.estado}` : ""}`}>
            <div className="cat-plan__cabeza">
              <div><h4>{p.nombre}</h4>{p.costo != null && <span>${numero(p.costo)} al mes</span>}</div>
              <Estado estado={p.estado} />
            </div>
            <div>
              <div className="cat-plan__vel"><b>{p.megas}</b><span>Mbps</span></div>
              <div className="cat-escala"><i style={{ width: `${Math.round((p.megas / tope) * 100)}%` }} /></div>
            </div>
            <dl className="cat-plan__datos">
              <div><dt>VLAN</dt><dd>{p.vlan ?? "—"}</dd></div>
              <div><dt>Tabla</dt><dd>{p.tabla ?? "—"}</dd></div>
              <div><dt>Clientes</dt><dd>{numero(p.clientes)}</dd></div>
              <div><dt>Del total</dt><dd>{(() => {
                if (!total) return "—";
                const pct = ((p.clientes || 0) / total) * 100;
                // Un plan con 5 clientes de 1 400 no tiene el 0%: tiene menos del 1.
                return pct > 0 && pct < 1 ? "<1%" : `${Math.round(pct)}%`;
              })()}</dd></div>
            </dl>
            {p.diferencias.map((d) => <span key={d} className={`cat-diferencia cat-diferencia--${p.estado}`}>{d}</span>)}
            <div className="cat-plan__pie">
              <span>CIR {numero(p.megas * 1024)} kbps</span>
              {p.estado === "falta" && p.tabla != null && (
                // El plan ya está en la base pero la OLT no tiene su tabla:
                // «Nuevo plan» no sirve, el plan ya existe.
                <Boton className="cat-chico" icono={Iconos.agregar} disabled={ocupado}
                  onClick={() => mandar(`/plan/${p.id}/tabla`, {}, "POST", `Tabla ${p.tabla} de «${p.nombre}» creada en la OLT.`)}>Crear su tabla</Boton>
              )}
              <Boton className="cat-chico" variante="secundario" icono={Iconos.editar} disabled={ocupado} onClick={() => setPanel({ tipo: "plan", plan: p })}>Editar</Boton>
            </div>
          </article>
        ))}
        <button type="button" className="cat-plan cat-plan--nuevo" onClick={() => setPanel({ tipo: "plan" })} disabled={ocupado}>
          <Iconos.agregar size={26} strokeWidth={1.8} /><b>Nuevo plan</b><span>nombre, megas y VLAN</span>
        </button>
      </div>
    </Seccion>
  );
}

function Perfiles({ datos, setPanel, ocupado }) {
  const { dba, linea, servicio } = datos.secciones;
  const crear = (tipo, item) => () => setPanel({ tipo: "crear", que: tipo, item });
  const ver = (tipo, item) => () => setPanel({ tipo: "ver", que: tipo, item });
  return (
    <>
      <Seccion
        icono={Iconos.potencia} titulo="Perfiles DBA" cuenta={dba.length}
        texto="Cuánto ancho de subida se reserva a cada T-CONT."
        ayuda="type1 fijo (gestión) · type2 asegurado (voz, IPTV) · type3 asegurado + máximo · type4 máximo (Internet). Los de fábrica de la OLT no aparecen: solo los nuestros."
        acciones={<Boton variante="secundario" icono={Iconos.agregar} onClick={() => setPanel({ tipo: "dba" })} disabled={ocupado}>Perfil DBA</Boton>}
      >
        <Tabla columna="Qué reserva" vacio="Todavía no hay perfiles DBA en el catálogo." filas={dba.map((d) => ({
          clave: d.numero, numero: d.numero, nombre: d.nombre,
          // Las ONTs de un DBA son las de los perfiles de línea que lo usan:
          // a él no lo referencia ninguna ONT directamente.
          sub: [TIPOS_DBA[d.tipo]?.texto, d.usadoPor?.length ? `lo usa ${d.usadoPor.join(", ")}` : "no lo usa ningún perfil"].join(" · "),
          estado: d.estado, diferencias: d.diferencias, onts: d.onts,
          detalle: <Cadena trozos={[{ texto: <><b>{d.tipo}</b></> }, ...TIPOS_DBA[d.tipo].campos.map((c) => ({ texto: <>{PALABRA[c]} <b>{numero(d[c])}</b> kbps</>, flecha: false }))]} />,
          accion: botonDe(d.estado, crear("dba", d), ver("dba", d), ocupado),
        }))} />
      </Seccion>

      <Seccion
        icono={Iconos.senal} titulo="Perfiles de línea" cuenta={linea.length}
        texto="Qué servicios lleva la ONT: cada uno es un T-CONT con su DBA y su GEM."
        ayuda="El de las altas lo tienen todas las ONTs y no se modifica: un cambio les llega a todas a la vez. Voz o IPTV van en un perfil de línea nuevo, y se pasa cliente por cliente."
        acciones={<Boton icono={Iconos.agregar} onClick={() => setPanel({ tipo: "linea" })} disabled={ocupado}>Perfil de línea</Boton>}
      >
        <Tabla columna="Servicios" vacio="Todavía no hay perfiles de línea en el catálogo." filas={linea.map((l) => ({
          clave: l.numero, numero: l.numero, nombre: l.nombre,
          sub: `separa por ${l.modo === "priority" ? "prioridad" : "VLAN"}${datos.globales?.lineprofile === l.numero ? " · el de las altas" : ""}`,
          estado: l.estado, diferencias: l.diferencias, onts: l.onts,
          detalle: (
            <div className="cat-servicios">
              {l.servicios.map((s) => (
                <Cadena key={s.tcont} trozos={[
                  { texto: <b>{s.nombre}</b> }, { texto: <>T-CONT <b>{s.tcont}</b></> }, { texto: <>DBA <b>{s.dba_numero}</b></> },
                  { texto: <>GEM <b>{s.gem}</b></> }, { texto: l.modo === "priority" ? <>prioridad <b>{s.prioridades}</b></> : <>VLAN <b>{s.vlan ?? "de los planes"}</b></> },
                ]} />
              ))}
            </div>
          ),
          accion: botonDe(l.estado, crear("linea", l), ver("linea", l), ocupado, l.requiere),
        }))} />
      </Seccion>

      <Seccion
        icono={Iconos.documento} titulo="Perfiles de servicio" cuenta={servicio.length}
        texto="Los puertos que se le configuran a la ONT."
        acciones={<Boton variante="secundario" icono={Iconos.agregar} onClick={() => setPanel({ tipo: "servicio" })} disabled={ocupado}>Perfil de servicio</Boton>}
      >
        <Tabla columna="Puertos" vacio="Todavía no hay perfiles de servicio en el catálogo." filas={servicio.map((s) => ({
          clave: s.numero, numero: s.numero, nombre: s.nombre, sub: datos.globales?.srvprofile === s.numero ? "el de las altas" : "",
          estado: s.estado, diferencias: s.diferencias, onts: s.onts,
          detalle: <Cadena trozos={[{ texto: <>eth adaptive <b>{s.eth}</b></> }, { texto: <>teléfono <b>{s.pots ? "sí" : "no"}</b></>, flecha: false }, { texto: <>ring check <b>{s.ring_check ? "on" : "off"}</b></>, flecha: false }]} />,
          accion: botonDe(s.estado, crear("servicio", s), ver("servicio", s), ocupado),
        }))} />
      </Seccion>
    </>
  );
}

function Puertos({ eth, banda }) {
  return (
    <span className="cat-puertos">
      <span className="cat-lan" aria-label={`${eth} puertos de red`}>{Array.from({ length: eth }).map((_, i) => <i key={i} />)}</span>
      {banda !== "nada" && <span className="cat-chip">2.4 G</span>}
      {banda === "doble" && <span className="cat-chip">5 G</span>}
    </span>
  );
}

function Modelos({ datos, setPanel, ocupado, mandar }) {
  const { modelos } = datos.secciones;
  const filas = [
    ...modelos.map((m) => ({
      clave: m.modelo, numero: m.numero, nombre: m.modelo,
      sub: `${m.eth} puerto${m.eth === 1 ? "" : "s"} · ${m.banda === "doble" ? "doble banda" : m.banda === "simple" ? "solo 2.4 G" : "sin WiFi"}${m.ontsRed ? ` · ${numero(m.ontsRed)} en la red` : ""}`,
      estado: m.estado, diferencias: m.diferencias, onts: m.onts,
      detalle: <Puertos eth={m.eth} banda={m.banda} />,
      accion: botonDe(m.estado, () => mandar("/crear-faltantes", { tipo: "modelos", numero: m.numero }, "POST", `Perfil de ${m.modelo} creado en la OLT.`), () => setPanel({ tipo: "ver", que: "modelos", item: m }), ocupado),
    })),
    ...datos.sinPerfil.map((m) => ({
      clave: `sin-${m.modelo}`, numero: null, nombre: m.modelo, sub: "no está en el catálogo", estado: "falta",
      diferencias: ["sin perfil: su alta se para en la WAN"], onts: m.onts,
      detalle: <span className="cat-tenue">—</span>,
      insignia: <Estado estado="falta" texto="sin perfil" />,
      accion: <Boton className="cat-chico" icono={Iconos.agregar} onClick={() => setPanel({ tipo: "modelo", modelo: m.modelo })} disabled={ocupado}>Añadir</Boton>,
    })),
  ];
  return (
    <Seccion
      icono={Iconos.onus} titulo="Modelos de ONT" cuenta={filas.length}
      texto="Qué puertos y qué WiFi salen a internet en cada modelo. Los modelos salen de las ONTs de la red."
      ayuda="WiFi 2.4 G es el índice 1 y 5 G el 5. «ONTs» es cuántas tienen ese perfil en la OLT."
      acciones={<Boton icono={Iconos.agregar} onClick={() => setPanel({ tipo: "modelo" })} disabled={ocupado}>Modelo</Boton>}
    >
      <Tabla columna="Puertos y WiFi" vacio="No hay modelos." filas={filas} />
    </Seccion>
  );
}

function Gestion({ datos, setPanel, ocupado, mandar }) {
  const { wan, tr069 } = datos.secciones;
  const activo = datos.tr069Activo;
  const ver = (tipo, item) => () => setPanel({ tipo: "ver", que: tipo, item });
  const crear = (tipo, item) => () => setPanel({ tipo: "crear", que: tipo, item });
  return (
    <>
      <Seccion
        icono={Iconos.antena} titulo="Perfil WAN" cuenta={wan.length}
        texto="El que usa el alta para la conexión PPPoE de la ONT."
        acciones={<Boton variante="secundario" icono={Iconos.agregar} onClick={() => setPanel({ tipo: "wan" })} disabled={ocupado}>Perfil WAN</Boton>}
      >
        <Tabla columna="Qué hace" vacio="Todavía no hay perfil WAN en el catálogo." filas={wan.map((w) => ({
          clave: w.numero, numero: w.numero, nombre: w.nombre, sub: "PPPoE en ruta", estado: w.estado, diferencias: w.diferencias, onts: w.onts,
          detalle: <Cadena trozos={[{ texto: "ruta" }, { texto: <b>nat {w.nat ? "enable" : "disable"}</b>, flecha: false }]} />,
          accion: botonDe(w.estado, crear("wan", w), ver("wan", w), ocupado),
        }))} />
      </Seccion>

      <Seccion
        icono={Iconos.ajustes} titulo="TR-069" cuenta={activo === false ? 0 : tr069.length}
        texto="Gestión remota de las ONTs desde el servidor ACS."
        acciones={activo !== false && <Boton variante="secundario" icono={Iconos.agregar} onClick={() => setPanel({ tipo: "tr069" })} disabled={ocupado}>Servidor</Boton>}
      >
        <div className="cat-pregunta">
          <p>¿Esta OLT tiene servidor TR-069?</p>
          <Ayuda texto={activo == null ? "Falta aplicar la migración 014 para guardarlo; mientras, el alta lo hace siempre." : "Con «No», el alta se salta TR-069 y solo abre el acceso web de la ONT."} />
          <div className="cat-elegir" role="group" aria-label="¿Hay servidor TR-069?">
            <button type="button" aria-pressed={activo !== false} disabled={ocupado || activo == null || activo !== false}
              onClick={() => mandar("/tr069", { activo: true }, "PUT", "TR-069 activado para esta OLT.")}>Sí</button>
            <button type="button" aria-pressed={activo === false} disabled={ocupado || activo == null || activo === false}
              onClick={() => mandar("/tr069", { activo: false }, "PUT", "Esta OLT ya no engancha las ONTs a TR-069.")}>No</button>
          </div>
        </div>
        {activo === false
          ? <div className="cat-fuera"><Iconos.info size={16} strokeWidth={2} />Esta OLT no engancha las ONTs a TR-069.</div>
          : (
            <Tabla columna="Servidor" vacio="Todavía no hay servidor TR-069 en el catálogo." filas={tr069.map((t) => ({
              clave: t.numero, numero: t.numero, nombre: t.nombre, sub: "servidor ACS", estado: t.estado, diferencias: t.diferencias, onts: t.onts,
              detalle: <Cadena trozos={[{ texto: <b>{t.url}</b> }]} />,
              accion: botonDe(t.estado, crear("tr069", t), ver("tr069", t), ocupado),
            }))} />
          )}
      </Seccion>
    </>
  );
}

/* ================= el panel lateral ================= */

function Campo({ id, etiqueta, ancho, nota, children }) {
  return (
    <label className={`cat-campo${ancho ? " cat-campo--ancho" : ""}`} htmlFor={id}>
      <span>{etiqueta}</span>{children}{nota && <small>{nota}</small>}
    </label>
  );
}

function Panel({ panel, datos, cerrar, mandar, ocupado }) {
  useEffect(() => {
    const tecla = (e) => { if (e.key === "Escape" && !ocupado) cerrar(); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [cerrar, ocupado]);

  const Contenido = {
    plan: PanelPlan, dba: PanelDba, linea: PanelLinea, servicio: PanelServicio,
    wan: PanelWan, tr069: PanelTr069, modelo: PanelModelo, ver: PanelVer, faltantes: PanelFaltantes,
    crear: PanelCrear, altas: PanelAltas,
  }[panel.tipo];
  return (
    <>
      <div className="cat-velo" onClick={() => !ocupado && cerrar()} />
      <aside className="cat-cajon" role="dialog" aria-modal="true" aria-label="Panel">
        <Contenido panel={panel} datos={datos} cerrar={cerrar} mandar={mandar} ocupado={ocupado} />
      </aside>
    </>
  );
}

function Marco({ icono: I, titulo, sub, cerrar, ocupado, pie, children }) {
  return (
    <>
      <header className="cat-cajon__cabeza">
        <span className="cat-tarjeta__icono"><I size={19} strokeWidth={1.9} /></span>
        <div><h3>{titulo}</h3>{sub && <p>{sub}</p>}</div>
        <button type="button" className="cat-cajon__cerrar" onClick={cerrar} disabled={ocupado} aria-label="Cerrar"><Iconos.cerrar size={18} /></button>
      </header>
      <div className="cat-cajon__cuerpo">{children}</div>
      <footer className="cat-cajon__pie">
        <Boton variante="secundario" onClick={cerrar} disabled={ocupado}>Cancelar</Boton>
        {pie}
      </footer>
    </>
  );
}

function Comandos({ olt, base, titulo = "En la OLT" }) {
  return (
    <>
      {olt?.length > 0 && <div className="cat-bloque"><h4><Iconos.olt size={14} />{titulo}</h4><pre className="sw-comandos">{olt.join("\n")}</pre></div>}
      {base && <div className="cat-bloque"><h4><Iconos.documento size={14} />En el catálogo</h4><pre className="sw-comandos">{base}</pre></div>}
    </>
  );
}

function PanelPlan({ panel, datos, cerrar, mandar, ocupado }) {
  const editando = panel.plan;
  const vlans = datos.vlans.filter((v) => v.uplink);
  const [f, setF] = useState(editando
    ? { nombre: editando.nombre, megas: String(editando.megas) }
    : { nombre: "", megas: "", costo: "", vlan: vlans[0]?.id ?? "", indice: String(datos.sugerencias.traffic) });
  const megas = Number(f.megas) || 0;
  const cambiaNombre = editando && f.nombre.trim() !== editando.nombre;
  const cambiaMegas = editando && megas !== editando.megas;
  const comandos = editando ? (cambiaMegas ? [ordenTraffic(editando.tabla, megas, true)] : []) : (megas && f.indice !== "" ? [ordenTraffic(f.indice, megas, false)] : []);
  const listo = editando ? (cambiaNombre || cambiaMegas) && f.nombre.trim() && megas > 0
    : f.nombre.trim() && megas > 0 && f.costo !== "" && f.vlan !== "" && f.indice !== "";
  return (
    <Marco icono={Iconos.planes} titulo={editando ? `Editar «${editando.nombre}»` : "Nuevo plan"}
      sub={editando ? "Solo nombre y megas." : "Se guarda en la base y se crea su tabla de tráfico en la OLT."}
      cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!listo}
        onClick={() => (editando
          ? mandar(`/plan/${editando.id}`, { nombre: f.nombre.trim(), megas }, "PUT", "Plan guardado.")
          : mandar("/plan", { ...f, nombre: f.nombre.trim() }, "POST", "Plan creado."))}>
        {editando ? "Guardar" : "Guardar y mandar a la OLT"}</Boton>}
    >
      <div className="cat-campos">
        <Campo id="pl-nombre" etiqueta="Nombre del plan" ancho><input id="pl-nombre" value={f.nombre} maxLength={40} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Ej. Ultra" /></Campo>
        <Campo id="pl-megas" etiqueta="Megas"><input id="pl-megas" type="number" min="1" value={f.megas} onChange={(e) => setF({ ...f, megas: e.target.value })} placeholder="Ej. 200" /></Campo>
        {!editando && (
          <>
            <Campo id="pl-costo" etiqueta="Precio al mes"><input id="pl-costo" type="number" min="0" value={f.costo} onChange={(e) => setF({ ...f, costo: e.target.value })} /></Campo>
            <Campo id="pl-vlan" etiqueta="VLAN" nota="solo las que salen por un uplink">
              <select id="pl-vlan" value={f.vlan} onChange={(e) => setF({ ...f, vlan: e.target.value })}>{vlans.map((v) => <option key={v.id} value={v.id}>{v.id}</option>)}</select>
            </Campo>
            <Campo id="pl-indice" etiqueta="Nº de tabla" nota="el primero libre"><input id="pl-indice" type="number" min="0" value={f.indice} onChange={(e) => setF({ ...f, indice: e.target.value })} /></Campo>
          </>
        )}
      </div>
      {!editando && vlans.length === 0 && (
        <p className="cat-nota cat-nota--aviso">Primero crea una VLAN con su uplink en la sección <b>Equipo</b>: el plan sale por ella.</p>
      )}
      {(cambiaNombre || cambiaMegas) && (
        <div className="cat-nota cat-nota--aviso">
          {cambiaNombre && <p>El nombre cambia también en los <b>{numero(editando.clientes)} clientes</b> del plan: <code>clientes.plan</code> guarda el nombre.</p>}
          {cambiaMegas && <p>Los <b>{numero(editando.clientes)} clientes</b> pasan de {editando.megas} a {megas} Mbps. Si la OLT no deja cambiar la tabla con clientes encima, lo dirá y no se toca nada.</p>}
        </div>
      )}
      <Comandos olt={comandos} base={editando ? null : f.nombre && `planes      «${f.nombre.trim()}» · ${megas} Mbps · $${f.costo || "?"}\nplanes_olt  VLAN ${f.vlan} · tabla ${f.indice}`} />
    </Marco>
  );
}

function PanelDba({ datos, cerrar, mandar, ocupado }) {
  const [f, setF] = useState({ nombre: "VOZ", numero: String(datos.sugerencias.dba), tipo: "type2", fijo: "", asegurado: "1024", maximo: "" });
  const presets = {
    Gestión: { nombre: "GESTION", tipo: "type1", fijo: "1024", asegurado: "", maximo: "" },
    Voz: { nombre: "VOZ", tipo: "type2", fijo: "", asegurado: "1024", maximo: "" },
    IPTV: { nombre: "IPTV", tipo: "type2", fijo: "", asegurado: "10240", maximo: "" },
    Internet: { nombre: "INTERNET", tipo: "type4", fijo: "", asegurado: "", maximo: "1024000" },
  };
  const reservado = TIPOS_DBA[f.tipo].campos.filter((c) => c !== "maximo").reduce((n, c) => n + (Number(f[c]) || 0), 0);
  const listo = f.nombre && f.numero !== "" && TIPOS_DBA[f.tipo].campos.every((c) => Number(f[c]) >= 128);
  return (
    <Marco icono={Iconos.potencia} titulo="Nuevo perfil DBA" sub="No toca a ninguna ONT hasta que un T-CONT lo usa." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!listo} onClick={() => mandar("/dba", f, "POST", `Perfil DBA ${f.numero} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-chips"><span>Partir de:</span>{Object.entries(presets).map(([k, v]) => <button key={k} type="button" onClick={() => setF({ ...f, ...v })}>{k}</button>)}</div>
      <div className="cat-campos">
        <Campo id="dba-nombre" etiqueta="Nombre"><input id="dba-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: sinEspacios(e.target.value) })} /></Campo>
        <Campo id="dba-numero" etiqueta="Nº" nota="el primero libre"><input id="dba-numero" type="number" min="1" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Campo>
        <Campo id="dba-tipo" etiqueta="Tipo" ancho>
          <select id="dba-tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>{Object.entries(TIPOS_DBA).map(([k, v]) => <option key={k} value={k}>{k} · {v.texto}</option>)}</select>
        </Campo>
        {TIPOS_DBA[f.tipo].campos.map((c) => (
          <Campo key={c} id={`dba-${c}`} etiqueta={{ fijo: "Fijo (kbps)", asegurado: "Asegurado (kbps)", maximo: "Máximo (kbps)" }[c]}>
            <input id={`dba-${c}`} type="number" min="128" step="128" value={f[c]} onChange={(e) => setF({ ...f, [c]: e.target.value })} />
          </Campo>
        ))}
      </div>
      <p className="cat-nota">{TIPOS_DBA[f.tipo].guia}{reservado > 0 && <> Con un PON lleno de 64 ONTs aparta <b>{Math.round((reservado * 64) / 1024)} Mbps</b> de los 1 244 de subida.</>}</p>
      <Comandos olt={comandosDba(f)} base={`catalogo_dba  «${f.nombre}» nº ${f.numero} · ${f.tipo}`} />
    </Marco>
  );
}

function PanelLinea({ datos, cerrar, mandar, ocupado }) {
  const { dba, linea, planes } = datos.secciones;
  const global = linea.find((l) => l.numero === datos.globales?.lineprofile) || linea[0];
  const internet = global?.servicios?.[0] || { nombre: "INTERNET", tcont: 3, gem: 3, dba_numero: dba[0]?.numero };
  const vlansPlanes = [...new Set(planes.map((p) => p.vlan).filter((v) => v != null))];
  const [f, setF] = useState({ nombre: "TRIPLE", numero: String(datos.sugerencias.linea), modo: "priority", extra: [] });
  const libres = [4, 5, 6, 7, 1, 2].filter((t) => t !== internet.tcont && !f.extra.some((s) => Number(s.tcont) === t));
  const anadir = () => {
    const t = libres[0];
    if (!t) return;
    const usadas = f.extra.map((s) => Number(s.prioridad));
    setF({ ...f, extra: [...f.extra, { nombre: f.extra.length ? "IPTV" : "VOZ", tcont: t, gem: 10 + t, dba: dba.find((d) => d.numero !== internet.dba_numero)?.numero ?? "", prioridad: [5, 4, 6, 7].find((p) => !usadas.includes(p)) ?? 5, vlan: "" }] });
  };
  const cambia = (i, k, v) => setF({ ...f, extra: f.extra.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const usadas = f.extra.map((s) => Number(s.prioridad));
  const restantes = [0, 1, 2, 3, 4, 5, 6, 7].filter((p) => !usadas.includes(p));
  const servicios = [
    { nombre: internet.nombre, tcont: internet.tcont, gem: internet.gem, dba: internet.dba_numero, prioridades: restantes.join(","), vlan: null },
    ...f.extra.map((s) => ({ nombre: s.nombre, tcont: Number(s.tcont), gem: Number(s.gem), dba: Number(s.dba), prioridades: String(s.prioridad), vlan: s.vlan ? Number(s.vlan) : null })),
  ];
  // Un DBA del catálogo que esta OLT no tiene no sirve: la OLT dejaría el
  // perfil a medias al llegar a su T-CONT.
  const dbaFalta = (n) => dba.find((d) => d.numero === Number(n))?.estado === "falta";
  const sinDba = [internet.dba_numero, ...f.extra.map((s) => s.dba)].filter((n) => n !== "" && dbaFalta(n));
  const listo = f.extra.length > 0 && sinDba.length === 0 && f.extra.every((s) => s.nombre && s.gem && s.dba !== "" && (f.modo === "priority" ? s.prioridad !== "" : s.vlan));
  return (
    <Marco icono={Iconos.senal} titulo="Nuevo perfil de línea" sub="Internet igual que el de las altas, más los servicios que elijas." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!listo}
        onClick={() => mandar("/linea", { numero: f.numero, nombre: f.nombre, modo: f.modo, servicios }, "POST", `Perfil de línea ${f.numero} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-campos">
        <Campo id="lp-nombre" etiqueta="Nombre"><input id="lp-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: sinEspacios(e.target.value) })} /></Campo>
        <Campo id="lp-numero" etiqueta="Nº" nota="el primero libre"><input id="lp-numero" type="number" min="1" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Campo>
        <Campo id="lp-modo" etiqueta="Separar los servicios por" ancho>
          <select id="lp-modo" value={f.modo} onChange={(e) => setF({ ...f, modo: e.target.value })}>
            <option value="priority">Prioridad 802.1p (como el de las altas)</option><option value="vlan">VLAN</option>
          </select>
        </Campo>
      </div>
      <p className="cat-nota">{f.modo === "priority"
        ? "La ONT marca cada servicio con su prioridad y esa prioridad decide el GEM. Internet se queda con las que sobran; un plan nuevo no obliga a tocar el perfil."
        : `Cada servicio va por su VLAN. Internet nombra las VLAN de los planes (${vlansPlanes.join(", ")}): un plan con VLAN nueva obliga a editar este perfil.`}</p>
      <div className="cat-servicios-forma">
        <div className="cat-servicio-fila cat-servicio-fila--fija">
          <b>{internet.nombre}</b><span>T-CONT {internet.tcont} · DBA {internet.dba_numero} · GEM {internet.gem} · {f.modo === "priority" ? `prioridad ${restantes.join(",")}` : "VLAN de los planes"}</span>
        </div>
        {f.extra.map((s, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="cat-servicio-fila">
            <input aria-label="Servicio" value={s.nombre} onChange={(e) => cambia(i, "nombre", sinEspacios(e.target.value).toUpperCase())} />
            <label>T-CONT<select aria-label="T-CONT" value={s.tcont} onChange={(e) => cambia(i, "tcont", Number(e.target.value))}>{[s.tcont, ...libres].filter((v, k, a) => a.indexOf(v) === k).sort().map((t) => <option key={t}>{t}</option>)}</select></label>
            <label>DBA<select aria-label="DBA" value={s.dba} onChange={(e) => cambia(i, "dba", Number(e.target.value))}><option value="">—</option>{dba.map((d) => <option key={d.numero} value={d.numero} disabled={d.estado === "falta"}>{d.numero} {d.nombre}{d.estado === "falta" ? " · falta en la OLT" : ""}</option>)}</select></label>
            <label>GEM<input aria-label="GEM" type="number" value={s.gem} onChange={(e) => cambia(i, "gem", e.target.value)} /></label>
            {f.modo === "priority"
              ? <label>Prio<input aria-label="Prioridad" type="number" min="0" max="7" value={s.prioridad} onChange={(e) => cambia(i, "prioridad", e.target.value)} /></label>
              : <label>VLAN<input aria-label="VLAN" type="number" value={s.vlan} onChange={(e) => cambia(i, "vlan", e.target.value)} /></label>}
            <button type="button" className="cat-quitar" aria-label="Quitar servicio" onClick={() => setF({ ...f, extra: f.extra.filter((_, j) => j !== i) })}><Iconos.cerrar size={14} /></button>
          </div>
        ))}
        <Boton variante="secundario" icono={Iconos.agregar} onClick={anadir} disabled={!libres.length}>Añadir servicio</Boton>
      </div>
      {sinDba.length > 0 && (
        <p className="cat-nota cat-nota--aviso">Primero crea en esta OLT el DBA {[...new Set(sinDba)].join(" y el ")}: el perfil lo usa en su T-CONT.</p>
      )}
      <p className="cat-nota cat-nota--info">No toca a ninguna ONT. Los clientes pasan a este perfil uno por uno, desde su ONU.</p>
      {f.extra.length > 0 && <Comandos olt={comandosLinea({ numero: f.numero, nombre: f.nombre, modo: f.modo, servicios }, vlansPlanes)} base={`catalogo_linea  «${f.nombre}» nº ${f.numero} · ${f.modo}\n${servicios.map((s) => `  ${s.nombre}  T-CONT ${s.tcont} · DBA ${s.dba} · GEM ${s.gem}`).join("\n")}`} />}
    </Marco>
  );
}

function PanelServicio({ datos, cerrar, mandar, ocupado }) {
  const [f, setF] = useState({ nombre: "TRIPLE", numero: String(datos.sugerencias.servicio), eth: 4, pots: true });
  return (
    <Marco icono={Iconos.documento} titulo="Nuevo perfil de servicio" sub="Una copia del GLOBAL con lo que cambie." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!f.nombre} onClick={() => mandar("/servicio", f, "POST", `Perfil de servicio ${f.numero} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-campos">
        <Campo id="sv-nombre" etiqueta="Nombre"><input id="sv-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: sinEspacios(e.target.value) })} /></Campo>
        <Campo id="sv-numero" etiqueta="Nº"><input id="sv-numero" type="number" min="1" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Campo>
        <Campo id="sv-eth" etiqueta="Puertos de red"><select id="sv-eth" value={f.eth} onChange={(e) => setF({ ...f, eth: Number(e.target.value) })}>{[4, 2, 1].map((n) => <option key={n}>{n}</option>)}</select></Campo>
        <Campo id="sv-pots" etiqueta="Teléfono"><select id="sv-pots" value={f.pots ? "si" : "no"} onChange={(e) => setF({ ...f, pots: e.target.value === "si" })}><option value="si">sí (voz)</option><option value="no">no</option></select></Campo>
      </div>
      <Comandos olt={comandosServicio(f)} base={`catalogo_servicio  «${f.nombre}» nº ${f.numero} · eth ${f.eth} · voz ${f.pots ? "sí" : "no"}`} />
    </Marco>
  );
}

function PanelWan({ datos, cerrar, mandar, ocupado }) {
  const [f, setF] = useState({ nombre: "", numero: String(datos.sugerencias.wan), nat: true });
  return (
    <Marco icono={Iconos.antena} titulo="Nuevo perfil WAN" sub="Cómo sale a internet la conexión PPPoE de la ONT." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!f.nombre} onClick={() => mandar("/wan", f, "POST", `Perfil WAN ${f.numero} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-campos">
        <Campo id="wan-nombre" etiqueta="Nombre" ancho><input id="wan-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: sinEspacios(e.target.value) })} placeholder="route-nat-enabled" /></Campo>
        <Campo id="wan-numero" etiqueta="Nº"><input id="wan-numero" type="number" min="0" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Campo>
        <Campo id="wan-nat" etiqueta="NAT"><select id="wan-nat" value={f.nat ? "si" : "no"} onChange={(e) => setF({ ...f, nat: e.target.value === "si" })}><option value="si">activado</option><option value="no">desactivado</option></select></Campo>
      </div>
      <Comandos olt={comandosWan(f)} base={`catalogo_wan  «${f.nombre || "?"}» nº ${f.numero} · nat ${f.nat ? "on" : "off"}`} />
    </Marco>
  );
}

function PanelTr069({ datos, cerrar, mandar, ocupado }) {
  const [f, setF] = useState({ nombre: "", numero: String(datos.sugerencias.tr069), url: "http://" });
  return (
    <Marco icono={Iconos.ajustes} titulo="Nuevo servidor TR-069" sub="El ACS al que se enganchan las ONTs." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!f.nombre || f.url.length < 10} onClick={() => mandar("/tr069/perfil", f, "POST", `Perfil TR-069 ${f.numero} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-campos">
        <Campo id="tr-nombre" etiqueta="Nombre"><input id="tr-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: sinEspacios(e.target.value) })} placeholder="acs-proxmox" /></Campo>
        <Campo id="tr-numero" etiqueta="Nº"><input id="tr-numero" type="number" min="1" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Campo>
        <Campo id="tr-url" etiqueta="URL del servidor" ancho><input id="tr-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value.trim() })} /></Campo>
      </div>
      <Comandos olt={comandosTr069(f)} base={`catalogo_tr069  «${f.nombre || "?"}» nº ${f.numero} · ${f.url}`} />
    </Marco>
  );
}

function PanelModelo({ panel, datos, cerrar, mandar, ocupado }) {
  const [f, setF] = useState({ modelo: panel.modelo || "", eth: 4, banda: "doble", id: String(datos.sugerencias.puertos) });
  const { eth, wlan } = puertosDe(f.eth, f.banda);
  const igual = datos.secciones.modelos.filter((m) => m.eth === Number(f.eth) && m.banda === f.banda).map((m) => m.modelo);
  return (
    <Marco icono={Iconos.onus} titulo={panel.modelo ? `Perfil de puertos para ${panel.modelo}` : "Nuevo modelo de ONT"} sub="Mira la etiqueta: cuántos puertos de red tiene y si da WiFi de 5 G." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!f.modelo || f.id === ""} onClick={() => mandar("/puertos", f, "POST", `Perfil de ${f.modelo} creado.`)}>Guardar y mandar a la OLT</Boton>}>
      <div className="cat-campos">
        <Campo id="md-modelo" etiqueta="Modelo" ancho><input id="md-modelo" value={f.modelo} onChange={(e) => setF({ ...f, modelo: e.target.value.trim().toUpperCase() })} /></Campo>
        <Campo id="md-eth" etiqueta="Puertos de red (LAN)" nota="los amarillos; no cuentes el de fibra"><select id="md-eth" value={f.eth} onChange={(e) => setF({ ...f, eth: Number(e.target.value) })}>{[4, 2, 1].map((n) => <option key={n}>{n}</option>)}</select></Campo>
        <Campo id="md-banda" etiqueta="WiFi"><select id="md-banda" value={f.banda} onChange={(e) => setF({ ...f, banda: e.target.value })}><option value="doble">doble banda (2.4 y 5 G)</option><option value="simple">solo 2.4 G</option><option value="nada">sin WiFi</option></select></Campo>
        <Campo id="md-id" etiqueta="Nº de perfil" nota="el primero libre"><input id="md-id" type="number" min="1" value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} /></Campo>
      </div>
      <div className="cat-nota"><Puertos eth={Number(f.eth)} banda={f.banda} />{igual.length ? <> Queda igual que <b>{igual.join(", ")}</b>; aun así se crea uno propio: la app lo busca por modelo.</> : " Ningún modelo del catálogo tiene esta combinación."}</div>
      <Comandos olt={comandosModelo(f)} base={`ont_policy_profiles  ${f.modelo || "?"} · nº ${f.id} · eth ${eth}${wlan ? ` · wlan ${wlan}` : ""}`} />
    </Marco>
  );
}

function PanelVer({ panel, cerrar, ocupado }) {
  const { item } = panel;
  const nombre = item.nombre || item.modelo;
  return (
    <Marco icono={Iconos.info} titulo={`«${nombre}»`} sub={item.estado === "aviso" ? "Está en la OLT, pero distinto al catálogo." : "Igual en la OLT."} cerrar={cerrar} ocupado={ocupado}>
      {item.diferencias?.length > 0 && (
        <div className="cat-nota cat-nota--aviso">
          <p><b>Qué no cuadra</b></p>
          <ul>{item.diferencias.map((d) => <li key={d}>{d}</li>)}</ul>
          <p>No se corrige solo: {item.onts ? `${numero(item.onts)} ONTs lo usan y un cambio les llegaría a todas.` : "revisa si conviene cambiar la OLT o el catálogo."}</p>
        </div>
      )}
      <Comandos olt={item.comandos} titulo="Cómo se crea en una OLT nueva" />
    </Marco>
  );
}

function PanelFaltantes({ datos, cerrar, mandar, ocupado }) {
  const S = datos.secciones;
  const grupos = [["Perfiles DBA", S.dba], ["Perfiles de línea", S.linea], ["Perfiles de servicio", S.servicio], ["Perfil WAN", S.wan],
    ["TR-069", datos.tr069Activo === false ? [] : S.tr069], ["Modelos de ONT", S.modelos]]
    .map(([t, l]) => [t, l.filter((x) => x.estado === "falta")]).filter(([, l]) => l.length);
  const comandos = grupos.flatMap(([, l]) => l.flatMap((x) => x.comandos || []));
  return (
    <Marco icono={Iconos.agregar} titulo="Crear lo que falta en esta OLT" sub="Del catálogo, en el orden en que lo usa el alta." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} onClick={() => mandar("/crear-faltantes", {}, "POST", "Lo que faltaba se creó en la OLT.")}>Mandar a la OLT</Boton>}>
      <ul className="cat-lista">{grupos.map(([t, l]) => <li key={t}><b>{t}</b><span>{l.map((x) => x.nombre || x.modelo).join(", ")}</span></li>)}</ul>
      <p className="cat-nota cat-nota--info">Las VLANs y su uplink se crean antes, en la sección Equipo. Lo que ya esté en la OLT se salta.</p>
      <Comandos olt={comandos} />
    </Marco>
  );
}

const RE_NOMBRE = /^[A-Za-z0-9_.-]{1,32}$/;

/** Crear en esta OLT un perfil del catálogo, eligiendo con qué nombre. */
function PanelCrear({ panel, cerrar, mandar, ocupado }) {
  const { que, item } = panel;
  const [nombre, setNombre] = useState(item.nombre);
  const valido = RE_NOMBRE.test(nombre);
  const primero = item.requiere || [];
  const comandos = (item.comandos || []).map((c, i) => (i === 0 ? c.replace(`profile-name "${item.nombre}"`, `profile-name "${nombre}"`) : c));
  return (
    <Marco icono={Iconos.agregar} titulo={`${TIPO_TITULO[que] || que} ${item.numero}`}
      sub="En esta OLT, con el número del catálogo: es el que usa el alta." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!valido || primero.length > 0}
        onClick={() => mandar("/crear-faltantes", { tipo: que, numero: item.numero, nombre }, "POST", `${TIPO_TITULO[que]} ${item.numero} «${nombre}» creado en la OLT.`)}>Mandar a la OLT</Boton>}>
      {primero.length > 0 && (
        <p className="cat-nota cat-nota--aviso">Primero crea {primero.join(" y ")}: sin eso la OLT deja este perfil a medias.</p>
      )}
      <div className="cat-campos">
        <Campo id="cr-nombre" etiqueta="Nombre" ancho nota={item.sugerido && nombre !== item.sugerido ? `la convención es «${item.sugerido}»: nombrar por el servicio` : "se nombra por el servicio que lleva"}>
          <input id="cr-nombre" value={nombre} maxLength={32} onChange={(e) => setNombre(sinEspacios(e.target.value))} />
        </Campo>
      </div>
      {item.sugerido && nombre !== item.sugerido && (
        <div className="cat-chips"><span>Sugerido:</span><button type="button" onClick={() => setNombre(item.sugerido)}>{item.sugerido}</button></div>
      )}
      {!valido && <p className="cat-nota cat-nota--aviso">Sin espacios ni comillas, hasta 32 caracteres: letras, números, - _ .</p>}
      <Comandos olt={comandos} />
    </Marco>
  );
}

/** El último paso de una OLT nueva: con qué perfiles da de alta. */
function PanelAltas({ datos, cerrar, mandar, ocupado }) {
  const S = datos.secciones;
  const p = datos.propuestaAltas;
  const linea = p && S.linea.find((l) => l.numero === p.lineprofile);
  const servicio = p && S.servicio.find((x) => x.numero === p.srvprofile);
  const gem = linea?.servicios.find((x) => x.gem === p?.gemport);
  const listo = linea?.estado === "ok" && servicio?.estado === "ok" && gem;
  return (
    <Marco icono={Iconos.confirmar} titulo="Perfiles de las altas" sub="Se guardan una vez; desde ahí esta OLT ya puede dar altas." cerrar={cerrar} ocupado={ocupado}
      pie={<Boton icono={Iconos.confirmar} cargando={ocupado} disabled={!listo}
        onClick={() => mandar("/altas", p, "PUT", "Listo: esta OLT ya puede dar altas.")}>Usar para las altas</Boton>}>
      {!p
        ? <p className="cat-nota cat-nota--aviso">El catálogo necesita al menos un perfil de línea y uno de servicio.</p>
        : (
          <>
            <ul className="cat-lista">
              <li><b>Perfil de línea</b><span>{p.lineprofile}{linea ? ` «${linea.nombre}»` : ""}</span></li>
              <li><b>Perfil de servicio</b><span>{p.srvprofile}{servicio ? ` «${servicio.nombre}»` : ""}</span></li>
              <li><b>GEM</b><span>{p.gemport}{gem ? ` · ${gem.nombre}` : ""}</span></li>
            </ul>
            {!listo && <p className="cat-nota cat-nota--aviso">Primero tienen que estar los dos perfiles en la OLT, iguales al catálogo.</p>}
            <p className="cat-nota cat-nota--info">Son los mismos números que usa la otra OLT. La VLAN y la velocidad de cada cliente siguen saliendo de su plan.</p>
          </>
        )}
    </Marco>
  );
}
